import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getProspect, getDossiers } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { CoverThumb, UserBadge, colorFromId } from '../components/DocumentPreview';
import { CoproprietesSection } from '../components/CoproprietesSection';
import { CreateDossierDialog } from '../components/CreateDossierDialog';
import { 
    ArrowLeft, 
    Phone, 
    Mail, 
    MapPin, 
    Pencil, 
    Plus,
    FolderKanban,
    UserCheck,
    UserX,
    Gift,
    Calendar,
    Building2
} from 'lucide-react';
import { toast } from 'sonner';

export const ProspectDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [prospect, setProspect] = useState(null);
    const [dossiers, setDossiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const canEdit = user?.role === 'admin' || user?.role === 'manager';

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        try {
            const [prospectRes, dossiersRes] = await Promise.all([
                getProspect(id),
                getDossiers({ prospect_id: id })
            ]);
            setProspect(prospectRes.data);
            setDossiers(dossiersRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
            navigate('/prospects');
        } finally {
            setLoading(false);
        }
    };

    const [createDossierOpen, setCreateDossierOpen] = useState(false);

    const handleCreateDossier = () => {
        setCreateDossierOpen(true);
    };

    /**
     * "Parrainage en 1 clic" — offers two paths :
     *  1) Instantly navigate to the prospect form pre-filled with this prospect as parrain.
     *  2) Copy a shareable referral URL to the clipboard so this client can pass it to a friend.
     * We do (2) opportunistically then trigger (1) so the commercial can start entering the referral.
     */
    const handleParrainage = async () => {
        const url = `${window.location.origin}/prospects/new?parrain_id=${id}`;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
                toast.success('Lien de parrainage copié — vous pouvez le partager avec ce client', { duration: 4000 });
            }
        } catch {
            // Clipboard may fail in insecure contexts — silently ignore
        }
        navigate(`/prospects/new?parrain_id=${id}`);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!prospect) return null;

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('fr-FR', { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
        });
    };

    return (
        <div className="space-y-6" data-testid="prospect-detail-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => navigate('/prospects')}
                        className="rounded-sm shrink-0"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-white text-xl sm:text-2xl font-bold shrink-0 ${prospect.is_client ? 'bg-[#16A34A]' : 'bg-[#E85A24]'}`}>
                            {prospect.prenom?.charAt(0)}{prospect.nom?.charAt(0)}
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                                <h1 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] tracking-tight break-words">
                                    {prospect.prenom} {prospect.nom}
                                </h1>
                                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-sm ${
                                    prospect.is_client 
                                        ? 'bg-[#16A34A]/10 text-[#16A34A]' 
                                        : 'bg-[#E85A24]/10 text-[#E85A24]'
                                }`}>
                                    {prospect.is_client ? (
                                        <><UserCheck className="w-3 h-3" /> Client</>
                                    ) : (
                                        <><UserX className="w-3 h-3" /> Prospect</>
                                    )}
                                </span>
                                {prospect.commercial_id && prospect.commercial_nom && (
                                    <span
                                        className="inline-flex items-center gap-2 px-2.5 py-1 text-xs font-semibold rounded-full text-white shadow-sm"
                                        style={{ backgroundColor: colorFromId(prospect.commercial_id) }}
                                        data-testid="assigned-badge"
                                    >
                                        <span
                                            className="w-5 h-5 rounded-full bg-white/25 flex items-center justify-center text-[10px] font-bold"
                                        >
                                            {(prospect.commercial_nom || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase()}
                                        </span>
                                        Assigné à {prospect.commercial_nom}
                                    </span>
                                )}
                            </div>
                            {prospect.societe && <p className="text-[#52525B]">{prospect.societe}</p>}
                        </div>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap sm:flex-nowrap shrink-0">
                    {canEdit && (
                        <Button 
                            variant="outline" 
                            onClick={() => navigate(`/prospects/${id}/edit`)}
                            className="rounded-sm flex-1 sm:flex-initial"
                            data-testid="edit-prospect-btn"
                        >
                            <Pencil className="w-4 h-4 mr-2" /> Modifier
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={handleParrainage}
                        className="rounded-sm flex-1 sm:flex-initial border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/10"
                        data-testid="parrainage-btn"
                        title="Créer un filleul de ce contact en un clic"
                    >
                        <Gift className="w-4 h-4 mr-2" /> Parrainage 1 clic
                    </Button>
                    <Button 
                        onClick={handleCreateDossier}
                        className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm flex-1 sm:flex-initial"
                        data-testid="create-dossier-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" /> Créer un dossier
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Informations */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Coordonnées */}
                    <div className="bg-white border border-[#E4E4E7] p-6">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Coordonnées</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center gap-3 p-3 bg-[#FAFAFA]">
                                <Phone className="w-5 h-5 text-[#E85A24]" />
                                <div>
                                    <p className="field-label">Téléphone</p>
                                    <p className="font-medium">{prospect.telephone || '-'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-[#FAFAFA]">
                                <Mail className="w-5 h-5 text-[#E85A24]" />
                                <div>
                                    <p className="field-label">Email</p>
                                    <p className="font-medium">{prospect.email || '-'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 bg-[#FAFAFA] md:col-span-2">
                                <MapPin className="w-5 h-5 text-[#E85A24]" />
                                <div>
                                    <p className="field-label">Adresse</p>
                                    <p className="font-medium">
                                        {prospect.adresse ? `${prospect.adresse}, ` : ''}
                                        {prospect.code_postal} {prospect.ville || '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Informations commerciales */}
                    <div className="bg-white border border-[#E4E4E7] p-6">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Informations commerciales</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <p className="field-label">Type de contact</p>
                                <p className="font-medium capitalize">{(prospect.type_contact || '').replace(/_/g, ' ') || '-'}</p>
                            </div>
                            <div>
                                <p className="field-label">Source</p>
                                <p className="font-medium">{prospect.source_lead || '-'}</p>
                            </div>
                            <div>
                                <p className="field-label">Type de travaux</p>
                                <p className="font-medium">{prospect.type_travaux || '-'}</p>
                            </div>
                            <div>
                                <p className="field-label">Statut du projet</p>
                                <p className="font-medium capitalize">{(prospect.statut_projet || '').replace(/_/g, ' ') || '-'}</p>
                            </div>
                            <div>
                                <p className="field-label">Créé le</p>
                                <p className="font-medium">{formatDate(prospect.created_at)}</p>
                            </div>
                        </div>
                        {(prospect.nature_projet || prospect.nature_besoin) && (
                            <div className="mt-4 pt-4 border-t border-[#E4E4E7]">
                                <p className="field-label">Nature du projet / besoin</p>
                                <p className="mt-1">{prospect.nature_projet || prospect.nature_besoin}</p>
                            </div>
                        )}
                        {prospect.commentaire && (
                            <div className="mt-4 pt-4 border-t border-[#E4E4E7]">
                                <p className="field-label">Commentaire</p>
                                <p className="mt-1">{prospect.commentaire}</p>
                            </div>
                        )}
                    </div>

                    {/* Dossiers */}
                    {(() => {
                        const closed = ['dossier_clos', 'reception_effectuee', 'satisfaction_envoyee', 'avis_google_demande', 'parrainage_obtenu', 'chantier_termine'];
                        const cancelled = ['devis_refuse', 'refuse'];
                        const enCours = dossiers.filter(d => !closed.includes(d.statut) && !cancelled.includes(d.statut));
                        const termines = dossiers.filter(d => closed.includes(d.statut));
                        const annules = dossiers.filter(d => cancelled.includes(d.statut));
                        const groups = [
                            { label: 'En cours', items: enCours, color: 'text-[#E85A24]' },
                            { label: 'Terminés', items: termines, color: 'text-green-600' },
                            { label: 'Annulés', items: annules, color: 'text-red-600' },
                        ].filter(g => g.items.length > 0);

                        return (
                            <div className="bg-white border border-[#E4E4E7] p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-semibold text-[#0A0A0A]">Dossiers ({dossiers.length})</h2>
                                </div>
                                {dossiers.length === 0 ? (
                                    <div className="text-center py-8 text-[#52525B]">
                                        <FolderKanban className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                        <p>Aucun dossier pour ce contact</p>
                                        <Button
                                            variant="link"
                                            onClick={handleCreateDossier}
                                            className="text-[#E85A24] mt-2"
                                        >
                                            Créer un dossier
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-5">
                                        {groups.map(group => (
                                            <div key={group.label}>
                                                <h3 className={`text-xs uppercase font-bold tracking-wide mb-2 ${group.color}`}>
                                                    {group.label} ({group.items.length})
                                                </h3>
                                                <div className="space-y-2">
                                                    {group.items.map(dossier => (
                                                        <Link
                                                            key={dossier.id}
                                                            to={`/dossiers/${dossier.id}`}
                                                            className="flex items-center justify-between p-4 bg-[#FAFAFA] hover:bg-[#F4F4F5] transition-colors gap-3"
                                                            data-testid={`dossier-${dossier.id}`}
                                                        >
                                                            <div className="flex items-center gap-3 min-w-0">
                                                                <CoverThumb dossierId={dossier.id} hasPhoto={dossier.has_cover_photo} size={48} />
                                                                <div className="min-w-0">
                                                                    <p className="font-medium text-[#0A0A0A] truncate">{dossier.titre}</p>
                                                                    <p className="text-sm text-[#52525B] truncate">{dossier.type_travaux || 'Non défini'}</p>
                                                                </div>
                                                            </div>
                                                            <span className="px-2 py-1 text-xs font-semibold bg-[#E85A24]/10 text-[#E85A24] rounded-sm shrink-0">
                                                                {dossier.statut?.replace(/_/g, ' ')}
                                                            </span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Copropriétés (only for syndics) */}
                    {prospect.type_contact === 'syndic' && !prospect.parent_prospect_id && (
                        <CoproprietesSection parentProspect={prospect} canEdit={canEdit} />
                    )}

                    {/* Parent syndic info (when this is a copropriété) */}
                    {prospect.parent_prospect_id && prospect.parent_nom && (
                        <div className="bg-[#7C3AED]/5 border border-[#7C3AED]/20 p-4 rounded-sm flex items-center gap-3" data-testid="parent-syndic-info">
                            <Building2 className="w-5 h-5 text-[#7C3AED] shrink-0" />
                            <div className="flex-1">
                                <p className="text-xs uppercase font-semibold text-[#7C3AED]">Copropriété rattachée à</p>
                                <Link
                                    to={`/prospects/${prospect.parent_prospect_id}`}
                                    className="font-medium text-[#0A0A0A] hover:text-[#7C3AED]"
                                >
                                    {prospect.parent_societe || prospect.parent_nom}
                                </Link>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Parrainage */}
                    {prospect.parrain_id && (
                        <div className="bg-white border border-[#E4E4E7] p-6">
                            <div className="flex items-center gap-2 mb-4">
                                <Gift className="w-5 h-5 text-[#E85A24]" />
                                <h3 className="font-semibold text-[#0A0A0A]">Parrainage</h3>
                            </div>
                            <p className="text-sm text-[#52525B]">
                                Ce contact a été parrainé
                            </p>
                        </div>
                    )}

                    {/* Dates clés */}
                    <div className="bg-white border border-[#E4E4E7] p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Calendar className="w-5 h-5 text-[#E85A24]" />
                            <h3 className="font-semibold text-[#0A0A0A]">Dates clés</h3>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <p className="field-label">Création</p>
                                <p className="text-sm">{formatDate(prospect.created_at)}</p>
                            </div>
                            {prospect.updated_at && (
                                <div>
                                    <p className="field-label">Dernière modification</p>
                                    <p className="text-sm">{formatDate(prospect.updated_at)}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <CreateDossierDialog
                open={createDossierOpen}
                onOpenChange={setCreateDossierOpen}
                prospect={prospect}
                onCreated={() => fetchData()}
            />
        </div>
    );
};
